import type { EditorView } from '@atlaskit/editor-prosemirror/view';

/**
 * Extra breathing room (px) kept between the scrolled-to position and the
 * viewport edge so content does not sit flush against it.
 */
export const SCROLL_MARGIN = 32;

/**
 * Calculates the cumulative offset from the editor DOM to its scroll container.
 * Walks up the DOM tree accumulating offsetTop values.
 *
 * @param editorDom - The editor's DOM element
 * @param scrollContainer - The scroll container element
 * @returns The total offset in pixels
 */
export function calculateOffsetTopFromScrollContainer(
	editorDom: Element,
	scrollContainer: HTMLElement,
): number {
	let offset = 0;
	let el: Element | null = editorDom;

	while (el && el !== scrollContainer) {
		if (el instanceof HTMLElement) {
			offset += el.offsetTop;
			el = el.offsetParent;
		} else {
			break;
		}
	}

	return offset;
}

/**
 * Content-space offset of `pos` from the top of `scrollParent`, using the same
 * `coordsAtPos` math as the scroll helpers (so tests can drive it without layout).
 */
function getPosOffsetInScrollContainer({
	view,
	pos,
	scrollParent,
}: {
	pos: number;
	scrollParent: HTMLElement;
	view: EditorView;
}): number {
	const startCoords = view.coordsAtPos(pos);
	const offsetTop = view.coordsAtPos(0).top;
	const offsetTopFromScrollContainer = calculateOffsetTopFromScrollContainer(
		view.dom,
		scrollParent,
	);
	return offsetTopFromScrollContainer + startCoords.top - offsetTop;
}

/**
 * Calculates the scroll position needed to show `pos` at the top of the viewport.
 *
 * @returns The target scrollTop value
 */
export function calculateTopScroll({
	view,
	pos,
	scrollParent,
}: {
	pos: number;
	scrollParent: HTMLElement;
	view: EditorView;
}): number {
	return getPosOffsetInScrollContainer({ view, pos, scrollParent }) - SCROLL_MARGIN;
}

/**
 * Calculates the scroll position needed to show `pos` at the bottom of the viewport.
 * Accounts for modal height so a modal anchored below `pos` is not hidden.
 *
 * @returns The target scrollTop value
 */
export function calculateBottomScroll({
	view,
	pos,
	scrollParent,
	modalHeight,
}: {
	modalHeight: number;
	pos: number;
	scrollParent: HTMLElement;
	view: EditorView;
}): number {
	const offsetTop = view.coordsAtPos(0).top;
	const offsetTopFromScrollContainer = calculateOffsetTopFromScrollContainer(
		view.dom,
		scrollParent,
	);

	// Handle edge case where coords could be 0 at the last position of doc
	let targetBottom = view.coordsAtPos(pos).bottom;
	if (targetBottom === 0 && pos > 0) {
		targetBottom = view.coordsAtPos(pos - 1).bottom;
	}

	return (
		offsetTopFromScrollContainer +
		targetBottom -
		offsetTop +
		modalHeight +
		SCROLL_MARGIN -
		scrollParent.offsetHeight
	);
}
